import React from 'react';
import { bestCompaniesContent } from '../data/bestCompaniesContent';
import LeadForm from './LeadForm';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiAward, FiArrowRight, FiCheck, FiX, FiInfo, FiClock, FiShield } = FiIcons;

const BestCompaniesPage = () => {
  const { meta, intro, comparisonTable, companies } = bestCompaniesContent;

  return (
    <div className="pt-20 bg-slate-50 min-h-screen">
      {/* Article Header */}
      <section className="bg-slate-900 py-16 lg:py-24 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-400/10 text-yellow-400 rounded-full text-sm font-bold mb-6 border border-yellow-400/20">
            <SafeIcon icon={FiAward} />
            <span>2026 Industry Report</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight text-white">
            {intro.title}
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed italic">
            "{intro.summary}"
          </p>
          
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400 border-t border-slate-800 pt-8 mt-8">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiClock} />
              <span>Last Updated: {meta.updatedDate}</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiShield} className="text-green-500" />
              <span>Verified Accuracy</span>
            </div>
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiInfo} />
              <span>Editorial Disclosure</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Main Article Body */}
          <div className="lg:col-span-8">
            <div className="prose-financial mb-16">
              <p className="text-lg leading-relaxed">{intro.content}</p>
              
              <h2 className="text-2xl font-bold mt-12 mb-6">Comparison at a Glance</h2>
              <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
                <table className="min-w-full divide-y divide-slate-200 m-0">
                  <thead className="bg-slate-100">
                    <tr>
                      {comparisonTable.headers.map((header, i) => (
                        <th key={i} className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {comparisonTable.rows.map((row, i) => (
                      <tr key={i} className={i === 0 ? "bg-yellow-50/30" : ""}>
                        <td className="px-6 py-4 text-sm font-bold text-slate-900">{row.name} {i === 0 && <span className="ml-2 text-[10px] bg-yellow-400 px-1.5 py-0.5 rounded uppercase">Best</span>}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{row.bestFor}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{row.min}</td>
                        <td className="px-6 py-4 text-sm text-yellow-600 font-bold flex items-center gap-1">
                          <SafeIcon icon={FiStar} /> {row.rating}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Individual Reviews */}
            <div className="space-y-16">
              {companies.map((company, index) => (
                <div key={company.id} className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden relative">
                  {index === 0 && (
                    <div className="absolute top-0 right-0 bg-yellow-400 text-slate-900 px-6 py-2 font-bold text-sm rounded-bl-2xl uppercase tracking-widest flex items-center gap-2">
                      <SafeIcon icon={FiAward} /> #1 Recommendation
                    </div>
                  )}
                  
                  <div className="p-8 md:p-12">
                    <div className="mb-6">
                      <span className="text-yellow-600 text-sm font-bold uppercase tracking-widest">{company.bestFor}</span>
                      <h3 className="text-3xl font-bold text-slate-900 mt-2">{company.name}</h3>
                      <p className="text-lg text-slate-500 italic mt-2">"{company.tagline}"</p>
                    </div>

                    <p className="text-slate-700 leading-relaxed mb-8">{company.overview}</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                      <div className="bg-green-50/50 p-6 rounded-2xl border border-green-100">
                        <h4 className="font-bold text-green-900 mb-4 flex items-center gap-2">
                          <SafeIcon icon={FiCheck} className="text-green-600" /> Pros
                        </h4>
                        <ul className="space-y-3">
                          {company.pros.map((pro, i) => (
                            <li key={i} className="text-sm text-green-800 flex items-start gap-2">
                              <span className="mt-1 text-green-500">•</span> {pro}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-red-50/50 p-6 rounded-2xl border border-red-100">
                        <h4 className="font-bold text-red-900 mb-4 flex items-center gap-2">
                          <SafeIcon icon={FiX} className="text-red-600" /> Cons
                        </h4>
                        <ul className="space-y-3">
                          {company.cons.map((con, i) => (
                            <li key={i} className="text-sm text-red-800 flex items-start gap-2">
                              <span className="mt-1 text-red-500">•</span> {con}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="space-y-6 mb-10 border-t border-slate-100 pt-8">
                      <div>
                        <h4 className="font-bold text-slate-900 mb-1">What Sets Them Apart</h4>
                        <p className="text-slate-600 text-sm">{company.uniqueness}</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-bold text-slate-900 mb-1 text-sm">Customer Reputation</h4>
                          <p className="text-slate-600 text-xs">{company.reputation}</p>
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-900 mb-1 text-sm">Storage & Security</h4>
                          <p className="text-slate-600 text-xs">{company.storage}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-4 border-t border-slate-100 pt-8">
                      <a 
                        href={company.ctaLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 bg-yellow-400 text-slate-900 font-bold rounded-xl hover:bg-yellow-300 transition-all shadow-md hover:shadow-lg"
                      >
                        {company.ctaText}
                        <SafeIcon icon={FiArrowRight} className="ml-2" />
                      </a>
                      {company.fullReviewLink && (
                        <a href={company.fullReviewLink} className="text-slate-500 hover:text-slate-900 text-sm font-medium underline underline-offset-4">
                          Read Full Review
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4">
            <div className="sticky top-28 space-y-8">
              <LeadForm 
                title="Request Your Free Gold IRA Kit"
                description="Compare our top 5 providers and receive educational materials on how to protect your 401(k) or IRA."
                ctaText="Get the Free Guide"
              />

              <div className="bg-slate-900 rounded-2xl p-8 text-white shadow-xl">
                <h4 className="text-xl font-bold mb-6 flex items-center gap-2 text-white">
                  <SafeIcon icon={FiShield} className="text-yellow-400" />
                  Our Ranking Criteria
                </h4>
                <ul className="space-y-4">
                  {[
                    "Transparency of Fees",
                    "Educational Resources",
                    "BCA & BBB Ratings",
                    "Paperwork Assistance",
                    "Storage Security Options"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-slate-400">
                      <SafeIcon icon={FiCheck} className="text-yellow-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BestCompaniesPage;